/**
 * Shared UI primitives for the NIPOST Pay subtree.
 *
 * Re-exports `PageHeader` + `StatusBadge` from the portal helpers and adds:
 *   - `formatNaira`    : display-only NGN formatter (string in / string out;
 *                       the wire payload always uses the source decimal string).
 *   - `DeferredNotice` : amber inline banner that quotes a missing or RBAC-
 *                       gated endpoint. Pattern from Phase 1c.
 *   - `RbacError`      : helper that maps an `ApiError` to a citizen-friendly
 *                       message for the keycloak-deferred case.
 *   - `KycTierBadge`   : renders the customer's wallet tier with the cap text.
 *   - `CustomerWalletQuery` hook — `useCustomerWallet(customerId)` driving the
 *                       same React Query key as Phase 1c's CustomerTopUpPage so
 *                       cache hits are shared across the subtree.
 */
import type { JSX } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';

import { ApiError } from '../../lib/api-client';
import {
  createOwnWallet,
  type WalletResponse,
} from '../../lib/api/wallet';

export { PageHeader, StatusBadge } from '../portal/PortalUI';

// ---------------------------------------------------------------------------
// Display-only formatters
// ---------------------------------------------------------------------------

/** Render a server-side decimal string as a localised NGN currency value.
 *  Display-only — the wire payload always carries the source decimal. */
export function formatNaira(amount: string | number | null | undefined): string {
  if (amount === null || amount === undefined || amount === '') {
    return '—';
  }
  // eslint-disable-next-line upmp/no-number-money -- display-only
  const n = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(n)) {
    return '—';
  }
   
  return `₦${n.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ---------------------------------------------------------------------------
// Deferred notice banner
// ---------------------------------------------------------------------------

interface DeferredNoticeProps {
  readonly heading: string;
  readonly body?: string;
  readonly endpoint?: string;
  readonly className?: string;
}

export function DeferredNotice({
  heading,
  body,
  endpoint,
  className = '',
}: DeferredNoticeProps): JSX.Element {
  return (
    <div
      className={`rounded-lg bg-amber-50 border border-amber-200 p-3 flex items-start gap-2 ${className}`}
      role="alert"
    >
      <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
      <div className="text-xs text-amber-900">
        <p className="font-semibold">{heading}</p>
        {body ? <p className="mt-0.5">{body}</p> : null}
        {endpoint ? (
          <p className="mt-0.5 font-mono text-[11px]">{endpoint}</p>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// RBAC / endpoint error mapping
// ---------------------------------------------------------------------------

/**
 * Convert an `ApiError` (or generic Error) into a short citizen-friendly
 * message. Matches the wording shipped by Phase 1c's CustomerTopUpPage so
 * the banners across the whole portal read the same.
 */
export function describeWalletError(err: Error | null | undefined): string {
  if (!err) {
    return '';
  }
  if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
    return 'You do not have permission to access wallet features. Please contact support.';
  }
  if (err instanceof ApiError && err.status === 404) {
    return 'No wallet found for this account yet — refresh to create one.';
  }
  if (err instanceof ApiError && err.status === 405) {
    return 'Wallet endpoint not available on this deployment.';
  }
  return err.message;
}

// ---------------------------------------------------------------------------
// Wallet query hook (shared cache key with Phase 1c top-up page)
// ---------------------------------------------------------------------------

export function customerWalletQueryKey(customerId: string): readonly [string, string] {
  return ['customer-wallet', customerId] as const;
}

export function useCustomerWallet(
  customerId: string,
  token: string | null,
): UseQueryResult<WalletResponse, Error> {
  return useQuery<WalletResponse, Error>({
    queryKey: customerWalletQueryKey(customerId),
    queryFn: () => createOwnWallet({ bearerToken: token ?? undefined }),
    enabled: customerId.length > 0 && token !== null,
    retry: false,
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// KYC tier helpers
// ---------------------------------------------------------------------------

/** Daily-inflow caps mirror service-financial-services
 *  `tier-cap-policy.ts` and the .env.example in `infrastructure/start/`. */
export const KYC_TIER_CAPS: Readonly<Record<string, { readonly label: string; readonly inflowCap: string; readonly description: string }>> = {
  TIER_0: {
    label: 'Tier 0',
    inflowCap: '0',
    description: 'No inflow allowed — complete KYC to start receiving.',
  },
  TIER_1: {
    label: 'Tier 1 — Basic',
    inflowCap: '50000',
    description: 'NIN + phone. Daily inflow up to ₦50,000.',
  },
  TIER_2: {
    label: 'Tier 2 — Standard',
    inflowCap: '300000',
    description: 'NIN + BVN. Daily inflow up to ₦300,000.',
  },
  TIER_3: {
    label: 'Tier 3 — Enhanced',
    inflowCap: 'unlimited',
    description: 'NIN + BVN + Government ID. Unlimited daily inflow.',
  },
};

interface KycTierBadgeProps {
  readonly tier: string | undefined;
  readonly className?: string;
}

const TIER_FALLBACK = {
  label: 'Unknown tier',
  inflowCap: '0',
  description: 'Tier metadata unavailable.',
} as const;

export function KycTierBadge({ tier, className = '' }: KycTierBadgeProps): JSX.Element {
  const meta = (tier ? KYC_TIER_CAPS[tier] : undefined) ?? KYC_TIER_CAPS['TIER_0'] ?? TIER_FALLBACK;
  const palette =
    tier === 'TIER_3'
      ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
      : tier === 'TIER_2'
        ? 'bg-nipost-green-light text-nipost-green border-nipost-green/20'
        : tier === 'TIER_1'
          ? 'bg-blue-50 text-blue-700 border-blue-200'
          : 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${palette} ${className}`}
      title={meta.description}
    >
      {meta.label}
    </span>
  );
}
